"use client";

import {
  Select,
  SelectItem,
  SelectPopup,
  SelectTrigger,
  SelectValue,
} from "@nyntax/design-system/components/ui/select";
import { cn } from "@nyntax/design-system/lib/utils";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { LuCheck, LuGift } from "react-icons/lu";

import type { CatalogSort } from "@/lib/catalog-sort";

interface CatalogToolbarProps {
  /** "12 courses". */
  countLabel: string;
  freeOnly: boolean;
  freeOnlyLabel: string;
  /** Where the Free only chip goes: the same view with the filter flipped. */
  freeToggleHref: string;
  sort: CatalogSort;
  sortLabel: string;
  /** Every sort with its label and the address that applies it. */
  sorts: { href: string; label: string; value: CatalogSort }[];
}

/**
 * The bar above the course grid: how many courses match, a Free only chip and
 * the sort. Both change the address, so the page stays server-rendered and a
 * filtered view can be shared.
 *
 * @param props - The count, the current sort and filter, and their addresses.
 * @returns The toolbar.
 */
export const CatalogToolbar = ({
  countLabel,
  freeOnly,
  freeOnlyLabel,
  freeToggleHref,
  sort,
  sortLabel,
  sorts,
}: CatalogToolbarProps) => {
  const router = useRouter();

  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <p aria-live="polite" className="text-muted-foreground text-sm font-medium">
        {countLabel}
      </p>
      <div className="flex items-center gap-2">
        <Link
          aria-current={freeOnly ? "true" : undefined}
          className={cn(
            "inline-flex h-9 items-center gap-1.5 rounded-lg border px-3 text-sm font-medium transition-colors",
            "focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none",
            freeOnly
              ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
              : "hover:bg-muted"
          )}
          href={freeToggleHref}
          scroll={false}
        >
          {freeOnly ? (
            <LuCheck aria-hidden="true" className="size-4" />
          ) : (
            <LuGift aria-hidden="true" className="size-4" />
          )}
          {freeOnlyLabel}
        </Link>
        <Select
          items={sorts.map(({ label, value }) => ({ label, value }))}
          onValueChange={(value) => {
            const next = sorts.find((entry) => entry.value === value);

            if (next !== undefined) {
              router.push(next.href, { scroll: false });
            }
          }}
          value={sort}
        >
          <SelectTrigger aria-label={sortLabel} className="w-48" size="sm">
            <span className="text-muted-foreground">{sortLabel}:</span>
            <SelectValue />
          </SelectTrigger>
          <SelectPopup>
            {sorts.map(({ label, value }) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectPopup>
        </Select>
      </div>
    </div>
  );
};
